/**
 * API builder for defining typed API endpoints
 */

import type {
  Client,
  HTTPMethod,
  HTTPAPIDefinition,
  HTTPAPIOptions,
  FormDataFile,
  WSAPIDefinition,
  SSEAPIDefinition,
  API,
  APIDefinition,
} from './types';
import { replacePath, appendQueryString } from './utils';

/**
 * Define HTTP API endpoint
 * @param method HTTP method
 * @param path URL path template
 * @param options Optional configuration (e.g., body serialization)
 * @returns HTTP API definition
 */
export function defHTTP<TParams = void, TResponse = any>(
  method: HTTPMethod,
  path: string,
  options?: HTTPAPIOptions
): HTTPAPIDefinition<TParams, TResponse> {
  return {
    type: 'http',
    method,
    path,
    options,
  };
}

/**
 * Define WebSocket API endpoint
 * @param path URL path template
 * @returns WebSocket API definition
 */
export function defWS<TParams = void, TData = any>(path: string): WSAPIDefinition<TParams, TData> {
  return {
    type: 'ws',
    path,
  };
}

/**
 * Define SSE API endpoint
 * @param path URL path template
 * @returns SSE API definition
 */
export function defSSE<TParams = void, TData = any>(
  path: string
): SSEAPIDefinition<TParams, TData> {
  return {
    type: 'sse',
    path,
  };
}

/**
 * Build API object from definitions
 * @param client API client instance
 * @param definitions API definitions object
 * @returns API object with typed methods
 */
export function apiBuilder<T>(client: Client, definitions: T): API<T> {
  const api: any = {};

  for (const [name, definition] of Object.entries(definitions as Record<string, APIDefinition>)) {
    if (definition.type === 'http') {
      // HTTP API
      const httpDef = definition as HTTPAPIDefinition<any, any>;

      api[name] = async (params: any = {}, options: any = {}) => {
        // Handle void params (undefined)
        const actualParams = params === undefined ? {} : params;
        const { url, remainingParams } = replacePath(httpDef.path, actualParams);

        // For GET and DELETE, use query params
        // For POST, PUT, PATCH, use body (but allow query params if no body)
        if (httpDef.method === 'GET' || httpDef.method === 'DELETE') {
          const fullUrl = appendQueryString(url, remainingParams);
          return httpDef.method === 'GET'
            ? client.http.get(fullUrl, options)
            : client.http.delete(fullUrl, options);
        } else {
          // POST, PUT, PATCH
          const method = httpDef.method.toLowerCase() as 'post' | 'put' | 'patch';

          // If remainingParams has data, use it as body
          // Otherwise, treat as query params
          const hasBody = Object.keys(remainingParams).length > 0;

          if (hasBody) {
            // Check if FormData serialization is requested
            if (httpDef.options?.bodySerializer === 'formData') {
              const formData = new FormData();
              for (const [key, value] of Object.entries(remainingParams)) {
                if (value instanceof File) {
                  // File object has its own name
                  formData.append(key, value, value.name);
                } else if (
                  value &&
                  typeof value === 'object' &&
                  'file' in value &&
                  'filename' in value &&
                  (value as FormDataFile).file instanceof Blob
                ) {
                  // FormDataFile wrapper: { file: Blob, filename: string }
                  const wrapper = value as FormDataFile;
                  formData.append(key, wrapper.file, wrapper.filename);
                } else if (value instanceof Blob) {
                  // Plain Blob without filename
                  formData.append(key, value);
                } else if (value !== undefined && value !== null) {
                  formData.append(key, String(value));
                }
              }
              return client.http[method](url, formData, options);
            }
            return client.http[method](url, remainingParams, options);
          } else {
            return client.http[method](url, undefined, options);
          }
        }
      };
    } else if (definition.type === 'ws') {
      // WebSocket API
      const wsDef = definition as WSAPIDefinition<any, any>;

      api[name] = (params: any = {}) => {
        const actualParams = params === undefined ? {} : params;
        const { url } = replacePath(wsDef.path, actualParams);
        return client.ws(url);
      };
    } else if (definition.type === 'sse') {
      // SSE API
      const sseDef = definition as SSEAPIDefinition<any, any>;

      api[name] = (params: any = {}) => {
        const actualParams = params === undefined ? {} : params;
        const { url } = replacePath(sseDef.path, actualParams);
        return client.sse(url);
      };
    }
  }

  // Expose the client instance
  api.client = client;

  return api as API<T>;
}
